import * as React from 'react';
import { useStore } from '@base-ui/utils/store';
import type { SchedulerEventOccurrence, SchedulerProcessedDay } from '../models';
import type { GetOccurrencesFromEventsParameters } from '../internals/utils/event-utils';
import {
  getDaysTheOccurrenceIsVisibleOn,
  getOccurrencesFromEvents,
} from '../internals/utils/event-utils';
import { useAdapterContext } from '../use-adapter-context';
import { getEndOfDayMs } from '../internals/utils/plain-date-utils/timeZone';
import { getDayNumber } from '../internals/utils/plain-date-utils/plainDate';
import { useEventCalendarStoreContext } from '../use-event-calendar-store-context';
import {
  schedulerEventSelectors,
  schedulerOtherSelectors,
  schedulerResourceSelectors,
} from '../scheduler-selectors';

/**
 * Gets all the event occurrences for the given days.
 * For recurring events, it expands them to get all the occurrences that fall within the given days.
 * It should be called once per view to get the occurrences for all the visible days in one go.
 * The returned value is a Map where the key is the day key and the value is the list of occurrences for that day.
 */
export function useEventOccurrencesGroupedByDay(
  parameters: useEventOccurrencesGroupedByDay.Parameters,
): useEventOccurrencesGroupedByDay.ReturnValue {
  const { days } = parameters;
  const adapter = useAdapterContext();
  const store = useEventCalendarStoreContext();
  const eventRangeIndex = useStore(store, schedulerEventSelectors.processedEventRangeIndex);
  const visibleResources = useStore(store, schedulerResourceSelectors.visibleMap);
  const displayTimezone = useStore(store, schedulerOtherSelectors.displayTimezone);
  const recurringEventsPlugin = useStore(store, schedulerOtherSelectors.recurringEventsPlugin);

  return React.useMemo(
    () =>
      innerGetEventOccurrencesGroupedByDay({
        adapter,
        days,
        eventRangeIndex,
        visibleResources,
        displayTimezone,
        recurringEventsPlugin,
      }),
    [adapter, days, eventRangeIndex, visibleResources, displayTimezone, recurringEventsPlugin],
  );
}

export namespace useEventOccurrencesGroupedByDay {
  export interface Parameters {
    /**
     * The days to get the occurrences for.
     */
    days: SchedulerProcessedDay[];
  }

  export type ReturnValue = Map<string, SchedulerEventOccurrence[]>;
}

/**
 * Do not use directly, use the `useEventOccurrencesGroupedByDay` hook instead.
 * This is only exported for testing purposes.
 */
export function innerGetEventOccurrencesGroupedByDay(
  parameters: Omit<GetOccurrencesFromEventsParameters, 'start' | 'end'> & {
    days: SchedulerProcessedDay[];
  },
): Map<string, SchedulerEventOccurrence[]> {
  const {
    adapter,
    days,
    eventRangeIndex,
    visibleResources,
    displayTimezone,
    recurringEventsPlugin,
  } = parameters;

  const occurrenceMap = new Map<string, SchedulerEventOccurrence[]>(
    days.map((day) => [day.key, []]),
  );

  if (days.length === 0) {
    return occurrenceMap;
  }

  const occurrences = getOccurrencesFromEvents({
    adapter,
    // The days start on their first instant.
    start: days[0].timestamp,
    end: getEndOfDayMs(days[days.length - 1].timestamp, displayTimezone),
    eventRangeIndex,
    visibleResources,
    displayTimezone,
    recurringEventsPlugin,
  });

  const dayNumbers = days.map((day) => getDayNumber(day.plainDate));
  for (const occurrence of occurrences) {
    const eventDays = getDaysTheOccurrenceIsVisibleOn(occurrence, days, dayNumbers);
    for (const dayKey of eventDays) {
      occurrenceMap.get(dayKey)!.push(occurrence);
    }
  }

  return occurrenceMap;
}
